import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OneTimeForm } from "@/components/OneTimeForm";
import { api, apiError, fmtDay, fmtPct } from "@/lib/api";

/**
 * The settle-once compliance figures, filled where the hole was found.
 *
 * It writes through the same `PATCH /compliance/profiles/{id}` the Compliance Data screen uses, so
 * there is still exactly one place a compliance figure lives.
 */
export const ComplianceFixDialog = ({ projectId, onClose, onSaved }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    if (!projectId) return;
    api
      .get(`/compliance/trend/${projectId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(apiError(e));
        onClose?.();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const card = data?.card;

  return (
    <Dialog open={!!projectId} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-none bg-card p-0"
        data-testid="compliance-fix-dialog"
      >
        {!data ? (
          <div className="flex h-48 items-center justify-center">
            <DialogTitle className="sr-only">Opening the compliance figures</DialogTitle>
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <DialogHeader className="border-b border-border p-6 pb-5 text-left">
              <div className="font-data flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                <span className="border border-border px-1.5 py-0.5">{card.plant}</span>
                <span className="bg-primary px-1.5 py-0.5 text-primary-foreground">
                  {card.phase || card.status || "—"}
                </span>
                <span>{card.code}</span>
              </div>
              <DialogTitle className="mt-2 font-display text-2xl font-black leading-tight tracking-tight">
                {card.title}
              </DialogTitle>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {card.compliance.days} day(s) of compliance on record · cumulative{" "}
                {fmtPct(card.compliance.cumulative_pct)} · last filled{" "}
                {card.last_updated ? fmtDay(card.last_updated) : "never"}. Declare the scale below
                and this usecase leaves the inconsistency list.
              </p>
            </DialogHeader>

            <div className="p-6">
              <OneTimeForm
                card={card}
                canEdit={data.can_record}
                testId={`incon-one-time-${card.code}`}
                onSaved={() => {
                  onSaved?.();
                  onClose?.();
                }}
              />
              {!data.can_record && (
                <p
                  className="mt-4 border border-border bg-muted/30 p-3 text-xs text-muted-foreground"
                  data-testid="compliance-fix-readonly"
                >
                  These figures belong to {card.plant} — its own SPOC or SDTD fills them.
                </p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
